import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchPublicPodcast, generateTranscriptForPodcast, syncMicroLearningPodcastState } from "src/redux/actions/coaching/podcast";

import {
  PodcastContext,
  PodcastContextProps,
} from "src/pages/coaching/components/podcast/context";
import { TopicsContainer } from "src/pages/coaching/components/podcast";
import { animated, useSpring } from "@react-spring/web";
import dateFormat from "dateformat";

import {
  enrollPodcast,
  fetchLearningRecordPodcast,
  fetchTranscriptsForLearningPodcast,
  startPodcast,
  updateCourseContentSession,
  updatePodcastSession,
} from "src/redux/actions/coaching/learners";
import { useNavigate } from "react-router-dom";

import { LearningRecordPodcast } from "src/types/coaching/learners";
import moment from "moment";
import { calculatePercentageRead } from "src/pages/coaching/components/podcast/components/player/analytics";
import { BannerView } from "src/components/banner";
import { FileService } from "src/utils/file.service";
import { TabBar } from "src/components/tabbar";
import Button from "src/components/button";
import { PodcastPlayer } from "src/pages/coaching/components/podcast/components/player";
import { PodcastTranscriptsContainer } from "src/pages/coaching/components/podcast/components/transcripts";

import _ from "lodash";
import cx from "classnames";
import styles from "./podcast.module.scss";

interface ViewPodcastContentProps {
  id: string | undefined;
  parentCourse: string | undefined;
}

export const ViewPodcastContent = ({
  id,
  parentCourse,
}: ViewPodcastContentProps) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [podcastState, setPodcastState] = useState({
    learningRecord: null,
    podcast: null,
  });

  const [podcastContext, setPodcastContext] =
    React.useState<PodcastContextProps>({
      id: id,
    });

  const [autoPlay, setAutoPlay] = useState(false);

  const enrollingPodcast = useSelector((state: any) => {
    const enrolling = _.get(
      state.coaching,
      `podcasts.podcast[${id}].enroll.enrolling`,
    );
    return enrolling;
  });

  const podcastEnrolledId = useSelector((state: any) => {
    return _.get(state.coaching, `podcasts.podcast[${id}].enroll.id`);
  });

  const learningRecordPodcast = useSelector((state: any) => {
    if (podcastEnrolledId) {
      return _.get(state.learners, `podcast[${podcastEnrolledId}].item`);
    }
    return null;
  });

  const publicPodcast = useSelector((state: any) => {
    return _.get(state.coaching, `podcasts.podcast[${id}].item`);
  });

  const podcast = learningRecordPodcast || publicPodcast;

  const loading = useSelector((state: any) =>
    _.get(state.coaching, `podcasts.podcast[${id}].loading`),
  );

  const generatingTranscript = useSelector((state: any) => {
    const generating = _.get(state.coaching, `podcasts.podcast[${id}].generateTranscript.generating`, false);
    return generating;
  });

  const processingPodcast = useSelector((state: any) => {
    const transcriptProcessing = _.get(publicPodcast, "transcriptProcessing", null);
    if(!transcriptProcessing) {
      return true;
    }
    const status = _.get(publicPodcast, "transcriptProcessing.status", null);
    return _.includes(["in_progress", "requested"], status);
  });

  const processingPodcastFailed = useMemo(() => {
    const status = _.get(publicPodcast, "transcriptProcessing.status", null);
    return _.includes(["failed"], status);
  }, [publicPodcast?.transcriptProcessing?.status]);

  const podcastTranscripts = useSelector((state: any) => {
    if (learningRecordPodcast) {
      return _.get(
        state.learners,
        `podcast[${learningRecordPodcast.id}].transcripts.item`,
      );
    }

    return null;
  });

  const transcriptGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `podcast[${id}].generateTranscript.generated`, null);
  });

  const audioGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `podcast[${id}].generateAudio.generated`, null);
  });

  const handleGeneratePodcast = () => {
    dispatch(generateTranscriptForPodcast({
      id: id!,
      generateAudio: true,
      draft: false,
    }));
  };


  const handleStartPodcast = () => {
    if (learningRecordPodcast && learningRecordPodcast.id) {
      dispatch(startPodcast(learningRecordPodcast.id));
    }
  };

  const handleEnrollPodcast = () => {
    if (podcast) {
      dispatch(
        enrollPodcast({
          course: podcast.id,
          parentCourse: parentCourse,
          type: "microlearning",
        }),
      );
      setAutoPlay(true);
    }
  };

  useEffect(() => {
    if (id && !podcast) {
      dispatch(fetchPublicPodcast({ id: id }));
    }

    setPodcastContext((prev) => {
      return {
        ...prev,
        id: id,
      };
    });

  }, [id]);

  useEffect(() => {
    if (publicPodcast) {
      setPodcastContext((prev) => {
        return {
          ...prev,
          startPodcast: handleStartPodcast,
          enrollPodcast: handleEnrollPodcast,
        };
      });

      setPodcastState((prev) => {
        return {
          ...prev,
          podcast: {
            ...podcast,
            id: podcast.id!,
            podcast: podcast,
            session: null,
            status: "NOT_ENROLLED",
          },
        };
      });
    }
  }, [publicPodcast]);

  useEffect(() => {
    if (podcastTranscripts) {
      setPodcastContext((prev) => {
        return {
          ...prev,
          transcripts: podcastTranscripts,
          startPodcast: handleStartPodcast,
        };
      });
    }
  }, [podcastTranscripts]);

  useEffect(() => {
    if (id && learningRecordPodcast) {
      dispatch(fetchTranscriptsForLearningPodcast(learningRecordPodcast.id));

      setPodcastContext((prev) => {
        return {
          ...prev,
          enrolledPodcastId: id,
          onStartPodcast: handleStartPodcast,
        };
      });
      setPodcastState((prev) => {
        return {
          ...prev,
          podcast: learningRecordPodcast,
        };
      });
    }
  }, [id, learningRecordPodcast?.id]);

  useEffect(() => {
    setPodcastContext((prev) => {
      return {
        ...prev,
        enrollingPodcast: enrollingPodcast,
      };
    });
  }, [enrollingPodcast]);

  useEffect(() => {
    if (podcastEnrolledId) {
      setPodcastState((prev) => {
        return {
          ...prev,
          podcast: {
            ...podcast,
            status: "NOT_STARTED",
          },
        };
      });
      
      dispatch(fetchLearningRecordPodcast(podcastEnrolledId));
    }
  }, [podcastEnrolledId]);

  useEffect(() => {
    if ((transcriptGenerated && audioGenerated && !learningRecordPodcast) && id) {
      dispatch(syncMicroLearningPodcastState({ id: id }));
    }
  }, [transcriptGenerated, audioGenerated]);

  if (loading || !podcast) {
    return (
      <LoadingPodcastPage />
    );
  }

  if (processingPodcastFailed) {
    return (
      <div className={styles.processingContainer}>
        <div className={styles.errorContainer}>
          <h1 className={styles.title}>podcast generation failed</h1>
          <p className={styles.description}>
              something went wrong while generating the podcast. please try again."
          </p>
          <div className={styles.actions}>
            <Button
              variant="courses-primary"
              className={styles.generateButton}
              text={generatingTranscript ? "retrying" : "retry"}
              disabled={generatingTranscript}
              icon="paperflite-reverse"
              onClick={handleGeneratePodcast}
            />
          </div>
        </div>       
      </div>
    );
  }

  if (processingPodcast) {
    return (
      <div className={styles.processingContainer}>
        <h1 className={styles.title}>
          generating&nbsp;<u>podcast</u>
          <img src="/images/coaching/dots_loading.gif" />
        </h1>
        <p className={styles.description}>
          We’re analyzing your content and creating a structured, bite-sized
          podcast. <br />
          This may take a few minutes. Feel free to go back—we&apos;ll notify
          you once it&apos;s ready.
        </p>
      </div>
    );
  }

  return (
    <PodcastContext.Provider value={podcastContext}>
      {podcastState.podcast && (
        <PodcastViewComponent
          podcast={podcastState.podcast}
          className={styles.podcastView}
          autoPlay={autoPlay}
        />
      )}
    </PodcastContext.Provider>
  );
};

interface PodcastViewComponentProps {
  podcast: LearningRecordPodcast;
  className?: string;
  autoPlay: boolean;
}
const PodcastViewComponent: React.FC<PodcastViewComponentProps> = ({
  podcast,
  className,
  autoPlay,
}) => {
  const podcastContext = React.useContext(PodcastContext);

  const [activeTab, setActiveTab] = useState(0);

  const name = useMemo(() => _.get(podcast, "podcast.name") || "", [podcast]);
  const description = useMemo(
    () => _.get(podcast, "podcast.description") || "",
    [podcast],
  );
  const banner = useMemo(() => _.get(podcast, "podcast.banner"), [podcast]);
  const topics = useMemo(
    () => _.get(podcast, "podcast.topics") || [],
    [podcast],
  );
  const status = useMemo(() => _.get(podcast, "status"), [podcast]);
  const podcastType = useMemo(() => _.get(podcast, "podcast.type"), [podcast]);
  const transcripts = podcastContext.transcripts;

  const { segments, duration } = useMemo(() => {
    const audio = _.get(podcast, "podcast.audio");
    const segments = _.get(audio, "segments", 1);
    const duration = _.get(audio, "duration", 0);

    return {
      segments: segments,
      duration: duration,
    };
  }, [podcast]);

  const initialTimeSpent = useMemo(() => {
    return _.get(podcast, "session.progress") || 0;
  }, [podcast?.id]);

  const handleBackClicked = () => {
    if (podcastContext.onBack) {
      podcastContext.onBack();
    }
  };

  const handlePodcastPlay = useCallback(() => {
    if (podcast.status === "NOT_STARTED" && podcastContext.startPodcast) {
      podcastContext.startPodcast();
    }
  }, [podcast, podcastContext.startPodcast]);

  const handleAnalyticsChange = useCallback(
    (event: any) => {
      const existingDatapoints = _.get(podcast, "session.dataPoints") || [];
      const datapoints = event.analytics?.datapoints || [];

      let merged = datapoints.map((dp: any) => {
        const existing = _.find(existingDatapoints, { index: dp.index });
        if (existing) {
          return {
            ...existing,
            views: (existing.views || 0) + (dp.views || 0),
            duration: (existing.duration || 0) + (dp.duration || 0),
            completed: existing.completed || dp.completed,
          };
        }

        return dp;
      });

      existingDatapoints.forEach((dp: any) => {
        if (!_.find(merged, { index: dp.index })) {
          merged.push(dp);
        }
      });

      merged = _.orderBy(merged, ["index", "asc"]) as any;
      const percentageCompleted = calculatePercentageRead(
        duration,
        segments,
        merged,
      );

      const updatedDuration = _.reduce(
        merged,
        (prev, current: any) => {
          return prev + current.duration;
        },
        0,
      );

      const updatedEvent = {
        ...event,
        analytics: {
          ...event.analytics,
          datapoints: merged,
          percentageRead: percentageCompleted,
          duration: updatedDuration,
        },
      };

      if (podcastContext.onAnalyticsChange) {
        podcastContext.onAnalyticsChange(updatedEvent);
      }
    },
    [podcast, podcastContext.onAnalyticsChange],
  );

  const handleEnrollPodcast = useCallback(() => {
    if (podcastContext.enrollPodcast) {
      podcastContext.enrollPodcast();
    }
  }, [podcast, podcastContext.enrollPodcast]);

  return (
    <div className={cx(styles.podcastContainer, className)}>
      <div className={styles.top}>
        <div className={styles.left}>
          <div className={styles.podcastDetails}>
            {podcastContext.onBack && (
              <div className={styles.back} onClick={handleBackClicked}>
                <i className="paperflite-left-arrow"></i>
                <span>back</span>
              </div>
            )}

            <div className={styles.details}>
              <div className={cx(styles.podcastHeader)}>
                <div className={styles.nameRow}>
                  <div className={styles.thumbnail}>
                    <BannerView
                      banner={banner as any}
                      defaultBanner="/images/assets/podcastThumbnail.jpg"
                      overlay={false}
                    />
                  </div>

                  <div className={styles.summary}>
                    <div className={styles.name}>{name}</div>
                    {status === "NOT_ENROLLED" && (
                      <div className={styles.actions}>
                        <Button
                          className={styles.startCourse}
                          text={
                            podcastContext.enrollingPodcast
                              ? "Preparing..."
                              : "Listen to podcast"
                          }
                          icon="paperflite-play"
                          disabled={podcastContext.enrollingPodcast}
                          onClick={handleEnrollPodcast}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.metadataRow}>
                  <div className={styles.podcastMetadata}>
                    <div className={styles.metadata}>
                      <i className="paperflite-clock" />
                      {FileService.formatDuration(duration, "long")}
                    </div>
                    <span className={styles.border} />
                    <div className={styles.metadata}>
                      <i className={cx(styles.speakers, "paperflite-rocket")} />
                      {podcastType || "Conversational"}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.topicsTranscriptContainer}>
                <TabBar
                  className={cx(styles.tabs)}
                  gap={32}
                  tabSize="equal"
                  active={activeTab}
                  onSelectionChange={(tab) => setActiveTab(tab)}
                >
                  <div
                    className={cx(styles.tab, {
                      [styles.active]: activeTab == 0,
                    })}
                  >
                    Details
                  </div>
                  {transcripts && (
                    <div
                      className={cx(styles.tab, {
                        [styles.active]: activeTab == 1,
                      })}
                    >
                      Transcript
                    </div>
                  )}
                </TabBar>
                <div className={styles.body}>
                  <TabSection selected={activeTab} index={0}>
                    <TopicsContainer podcast={podcast} />
                  </TabSection>
                  {transcripts && (
                    <TabSection selected={activeTab} index={1}>
                      <div className={styles.transcripts}>
                        <PodcastTranscriptsContainer
                          loading={false}
                          transcripts={transcripts}
                        />
                      </div>
                    </TabSection>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.bottom}>
        {podcast.podcast && status !== "NOT_ENROLLED" && (
          <PodcastPlayer
            podcast={podcast.podcast}
            initialTimeSpent={0}
            autoPlay={autoPlay}
            onPlay={handlePodcastPlay}
            onAnalyticsChange={() => {}}
          />
        )}
      </div>
    </div>
  );
};

interface LoadingPodcastPageProps {
  onBack?: () => void;
}

export const LoadingPodcastPage: React.FC<LoadingPodcastPageProps> = ({
  onBack,
}) => {
  return (
    <div className={styles.podcastContainer}>
      <div className={styles.top}>
        <div className={styles.left}>
          <div className={styles.podcastDetails}>
            {onBack && (
              <div
                className={styles.back}
                onClick={() => {
                  if (onBack) {
                    onBack();
                  }
                }}
              >
                <i className="paperflite-left-arrow"></i>
                <span>my courses</span>
              </div>
            )}
            <div
              className={cx(
                styles.loadingContainer,
                styles.details,
                styles.placeholder,
              )}
            >
              <div className={cx(styles.podcastHeader)}>
                <div className={cx(styles.thumbnail, styles.block)}></div>

                <div className={styles.summary}>
                  <div className={cx(styles.name, styles.block)}></div>
                  <div className={styles.podcastMetadata}>
                    <div className={styles.metadata}>
                      <i className="paperflite-clock" />
                      <div className={cx(styles.block, styles.label)}></div>
                    </div>
                    <span className={styles.border} />
                    <div className={styles.metadata}>
                      <div className={cx(styles.block, styles.label)}></div>
                    </div>
                    <span className={styles.border} />
                    <div className={styles.metadata}>
                      <div className={cx(styles.block, styles.label)}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.description}>
                <p className={cx(styles.title, styles.block)}></p>
                <div className={styles.descriptionContent}>
                  <div className={cx(styles.block, styles.line)}></div>
                  <div className={cx(styles.block, styles.line)}></div>
                  <div className={cx(styles.block, styles.line)}></div>
                </div>
              </div>

              <div className={styles.topics}>
                <div className={cx(styles.title, styles.block)}></div>
                <div className={styles.topicsContent}>
                  {_.times(5, (index: number) => (
                    <div key={index} className={styles.topic}>
                      <span className={cx(styles.index, styles.block)} />
                      <span className={styles.separator}>/</span>
                      <span className={cx(styles.name, styles.block)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      transform: active ? `translate3d(0,0,0)` : `translate3d(0, 100px, 0)`,
    },
    [active],
  );

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {active && children}
    </animated.div>
  );
};
